import { Component, Input, SimpleChanges } from '@angular/core'
import { XHR, XHRInitial, XHRLoading, XHRSuccess } from '@app/core/_models/xhr'
import {
    AvailableTimesService,
    PartnerAtTimeSlot,
    PartnersAtTimeSlotAvailabilityResponse,
} from '@app/core/_services/available-times.service'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { faLock } from '@fortawesome/pro-regular-svg-icons'

import { UserService } from '@app/core/_services/user.service'
import { forkJoin, Subject, take, takeUntil } from 'rxjs'
import {
    BookSessionPreferences,
    ConfirmedSessionsResponse,
    Container,
    SESSION_ACTIVITY_TYPE_ANYTHING,
    User,
} from '@app/core/_models'
import {
    BookSessionInfo,
    BookSessionsService,
    CancelSessionService,
    ConfirmedSessionsService,
    SelectedSession,
    SelectedSessionService,
    UtilsService,
} from '@app/core/_services'
import {
    AnalyticsService,
    BookingSource,
    SessionTileSource,
} from '@app/core/_services/analytics.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { UserDateFormattingService } from '@app/core/_services/user-date-formatting.service'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { StandardsModule } from '@app/standards-v2/standards.module'
import { CommonModule } from '@angular/common'
import { LoadingIndicatorComponent } from '@app/shared/components/loading-indicator/loading-indicator.component'
import { ScrollPanelModule } from 'primeng/scrollpanel'
import { TimeslotPartnerItemComponent } from './components/timeslot-partner-item/timeslot-partner-item.component'
import { ButtonModule } from 'primeng/button'
import { ContentStackService } from '@app/core/_services/content-stack.service'

const INVALID_LIST_ITEM_SELECTED = -1

const DIRECT_BOOK_TEXT = 'This person will be your partner unless one of you cancels'
const NON_DIRECT_BOOK_TEXT =
    'We’ll match you with this person or another Focusmate community member'
const COMMUNITY_BOOK_TEXT = 'We’ll match you with a Focusmate community member'
const PANEL_NAME = 'Timeslot Partners'

@Component({
    selector: 'app-rp-timeslot-partners',
    imports: [
        ButtonModule,
        TimeslotPartnerItemComponent,
        FontAwesomeModule,
        LoadingIndicatorComponent,
        StandardsModule,
        ScrollPanelModule,
        CommonModule,
    ],
    templateUrl: './rp-timeslot-partners.component.html',
    styleUrl: './rp-timeslot-partners.component.scss',
})
export class RpTimeslotPartnersComponent {
    @Input() sessionTime: number
    @Input() sessionDuration: number
    private ngUnsubscribe: Subject<any> = new Subject<any>()

    public currentUser: User = new User()
    public faLock = faLock
    public xhrPartners: XHR<PartnersAtTimeSlotAvailabilityResponse> = XHRInitial
    public loading: boolean = false
    public sessionDateAndDurationStr: string
    public sessionTimeStr: string

    public directBookPartnerList: PartnerAtTimeSlot[] = []
    public directBookPartnerListItemSelected: number = INVALID_LIST_ITEM_SELECTED
    public mostLikelyRandomPartner: PartnerAtTimeSlot
    public ismostLikelyRandomPartnerSelected: boolean = false

    // Footer text and link vary based on selection
    public footerLink: string
    public footerText: string

    private checkIfSelectedSessionCleared = false

    constructor(
        private availableTimesService: AvailableTimesService,
        private userService: UserService,
        private bookSessionService: BookSessionsService,
        private dialogService: DialogService,
        private cancelSessionService: CancelSessionService,
        private utilsService: UtilsService,
        private analyticsService: AnalyticsService,
        private sessionSettingsService: SessionSettingsService,
        private dateFormat: UserDateFormattingService,
        private contentStackService: ContentStackService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private selectedSessionService: SelectedSessionService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['sessionTime']) {
            if (!changes['sessionTime'].isFirstChange()) {
                this.initializeComponent()
            }
        } else if (changes['sessionDuration']) {
            if (!changes['sessionDuration'].isFirstChange()) {
                this.initializeComponent()
            }
        }
    }

    ngOnInit(): void {
        this.initializeComponent()
    }

    ngAfterViewInit() {
        this.confirmedSessionsService.confirmedSessionsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((confirmedSessions: Container<ConfirmedSessionsResponse>) => {
                // If the session timeslot we are looking at now gets booked, close
                // the panel
                const sessions: ConfirmedSessionsResponse[] = confirmedSessions.getArray()
                if (sessions.some((session) => session.sessionTime === this.sessionTime)) {
                    this.contentStackService.popContent()
                }
            })

        this.selectedSessionService.selectedSessions$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedSession: SelectedSession[]) => {
                // If the session timeslot we are looking at now gets booked, close
                // the panel

                if (!selectedSession.some((session) => session.sessionTime === this.sessionTime)) {
                    if (this.checkIfSelectedSessionCleared) {
                        this.contentStackService.popContent()
                    } else {
                        this.checkIfSelectedSessionCleared = true
                    }
                }
            })
    }

    private initializeComponent(): void {
        this.userService.currentUser.pipe(take(1)).subscribe((currentUser) => {
            this.currentUser = currentUser

            this.sessionTimeStr = this.dateFormat.format(this.sessionTime, 'h:mma')
            this.sessionDateAndDurationStr = `${this.sessionDuration / 1000 / 60} min · ${moment(this.sessionTime).format('dddd, MMM Do')}`
            this.populatePartnerList(true)
        })
    }

    private populatePartnerList(sendViewedModalEvent: boolean = false) {
        this.directBookPartnerList.length = 0
        this.mostLikelyRandomPartner = null
        this.xhrPartners = XHRLoading
        this.availableTimesService
            .queryAvailablePartnersAtTimeslot(this.sessionTime, this.sessionDuration)
            .subscribe((response) => {
                this.xhrPartners = XHRSuccess(response)
                this.parsePartnerList(this.xhrPartners.data.availability.partners)
                if (sendViewedModalEvent) {
                    this.analyticsService.logLoadedDynamicPanelEvent({
                        panel: PANEL_NAME,
                        count: this.directBookPartnerList.length,
                    })
                }
            })
    }

    private filterSessionBasedOnQuietModeSettings(partner: PartnerAtTimeSlot): boolean {
        /* Do not include sessions in this list if the following conditions are met:
		1. Potential partner has quietModeMatchAllowed setting set to False
		2. Current user has selected quietMode
		3. Potential partner's session is not set to Quiet Mode
		*/
        if (
            !partner.quietModeMatchAllowed &&
            !partner.preferences.quietMode.value &&
            this.sessionSettingsService.quietModeEnabled()
        ) {
            return true
        }
        return false
    }

    parsePartnerList(partners: PartnerAtTimeSlot[]) {
        let groupMatchFound = false

        for (let partner of partners) {
            if (this.filterSessionBasedOnQuietModeSettings(partner)) {
                continue
            }

            if (partner.saved && partner.sharedAvailability) {
                this.directBookPartnerList.push(partner)
            } else if (!this.mostLikelyRandomPartner) {
                this.mostLikelyRandomPartner = partner
                groupMatchFound = this.utilsService.usersShareGroup(
                    this.currentUser.group,
                    partner.groups,
                )
            } else if (!groupMatchFound) {
                if (this.utilsService.usersShareGroup(this.currentUser.group, partner.groups)) {
                    this.mostLikelyRandomPartner = partner
                    groupMatchFound = true
                }
            }
        }

        // Reverse the list so that the most recently booked
        // users are shown first, as per requirements
        this.directBookPartnerList.reverse()

        this.determineInitialSelection()
    }

    private resetSelection() {
        this.directBookPartnerListItemSelected = INVALID_LIST_ITEM_SELECTED
        this.ismostLikelyRandomPartnerSelected = false
    }

    private determineInitialSelection() {
        this.resetSelection()
        if (this.directBookPartnerList.length === 0) {
            this.ismostLikelyRandomPartnerSelected = true
        } else {
            this.directBookPartnerListItemSelected = 0
        }
        this.setFooterContent()
    }

    setFooterContent() {
        if (this.directBookPartnerListItemSelected >= 0) {
            this.footerText = DIRECT_BOOK_TEXT
        } else if (this.mostLikelyRandomPartner) {
            this.footerText = NON_DIRECT_BOOK_TEXT
        } else {
            this.footerText = COMMUNITY_BOOK_TEXT
        }
    }

    public onClickUpdateDirectBookPartnerListItemSelected(index: number) {
        this.resetSelection()
        this.directBookPartnerListItemSelected = index
        this.setFooterContent()
    }

    public onClickSetIsmostLikelyRandomPartnerSelected() {
        this.resetSelection()
        this.ismostLikelyRandomPartnerSelected = true
        this.setFooterContent()
    }

    public onClickedBookSession(): void {
        this.loading = true
        this.bookSession()
    }

    private bookSession() {
        let userId = null
        if (this.directBookPartnerListItemSelected >= 0) {
            userId = this.directBookPartnerList[this.directBookPartnerListItemSelected].userId
        }
        const preferences: BookSessionPreferences = {
            favorites: {
                value: this.sessionSettingsService.getSessionSettingFavoritesPreference(),
            },
            quietMode: {
                value: this.sessionSettingsService.quietModeEnabled(),
            },
        }

        let bookSessionInfo: BookSessionInfo = {
            duration: this.sessionDuration,
            sessionTime: this.sessionTime,
            userAvailabilityCode: null,
            title: null,
            userId: userId,
            preferences,
            activityType: this.sessionSettingsService.getSessionSettingActivityType(),
        }

        this.bookSessionService
            .bookSessions(
                [bookSessionInfo],
                this.currentUser.timeZone,
                BookingSource.TimeSlotPartnerModal,
            )
            .subscribe({
                next: (v) => this.receivedBookSessionResult(v),
                error: (e) => this.receivedBookSessionError(e),
                complete: () => this.receivedBookSessionComplete(),
            })
    }

    public receivedBookSessionResult(result) {
        let sessionTime = this.sessionTime
        let duration = this.sessionDuration
        let parsedRes = this.bookSessionService.parseBookResponse(result)

        let userId = null
        if (this.directBookPartnerListItemSelected >= 0) {
            userId = this.directBookPartnerList[this.directBookPartnerListItemSelected].userId
        }

        if (parsedRes['numSuccess'] !== 0) {
            this.dialogService.openConfirmTransaction('Session booked!')
        }

        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (parsedRes['sessionConflict'] === true) {
            const conflictTimes = parsedRes['conflicts'].map((c) => c.sessionTime)
            this.dialogService
                .openSessionConflictWithCancel(conflictTimes, this.currentUser.timeZone)
                .afterClosed()
                .pipe(take(1))
                .subscribe((actionResult) => {
                    if (actionResult.action === 'cancelAndBook') {
                        const preferences: BookSessionPreferences = {
                            favorites: {
                                value: this.sessionSettingsService.getSessionSettingFavoritesPreference(),
                            },
                            quietMode: {
                                value: this.sessionSettingsService.quietModeEnabled(),
                            },
                        }

                        let bookSessionInfo: BookSessionInfo = {
                            sessionTime: sessionTime,
                            userAvailabilityCode: '',
                            duration: duration,
                            userId: userId,
                            title: '',
                            preferences,
                            activityType: SESSION_ACTIVITY_TYPE_ANYTHING,
                        }
                        const idsToCancel = parsedRes['conflicts'].map(
                            (conflict) => conflict.externalId,
                        )
                        this.bookAndCancel(bookSessionInfo, idsToCancel)
                    }
                })
        } else if (parsedRes['inviterNoLongerAvailable'] === true) {
            this.dialogService.openInviterMeetingNoLongerAvailable()
            this.populatePartnerList()
        } else if (parsedRes['alreadyStarted'] === true) {
            this.dialogService.openBookFailedSessionAlreadyStarted()
            return
        }
        this.resetSpinnerButton()
    }

    private resetSpinnerButton() {
        this.loading = false
    }

    public receivedBookSessionError(e) {
        this.resetSpinnerButton()
    }

    public receivedBookSessionComplete() {
        this.resetSpinnerButton()
    }

    private bookAndCancel(bookInfo: BookSessionInfo, idsToCancel: string[]) {
        let cancelRequests = []
        for (let sessionTime of idsToCancel) {
            cancelRequests.push(
                this.cancelSessionService.cancelSession(
                    sessionTime,
                    SessionTileSource.AvailabilityConflictModal,
                ),
            )
        }

        forkJoin(cancelRequests).subscribe({
            next: (v) => this.bookSession(),
            error: (e) => this.bookSession(),
        })
    }

    onClickTrackLearnMoreSelected() {
        this.analyticsService.logClickedEvent('Learn more', PANEL_NAME)
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
